import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Alert,
  Chip,
  Snackbar,
  Paper
} from '@mui/material'
import {
  Add as AddIcon,
  Info as InfoIcon,
  AttachMoney as PriceIcon,
  Inventory as StatusIcon,
  CheckBox as SelectIcon,
  MoveDown as BulkMoveIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material'
import {
  useSearchCigarsQuery,
  useGetBrandsQuery,
  useGetCigarStatsQuery,
  useGetCigarsQuery,
  Cigar,
} from '@store/api/cigarApi'
import { useAppSelector } from '@store/hooks'
import { useOfflineCollection } from '@hooks/useOfflineCollection'
import { filterOfflineCigars, paginateCigars } from '@/lib/offline/filterCigars'
import CigarForm from '@components/cigars/CigarForm'
import SessionForm from '@components/sessions/SessionForm'
import CigarDetailsDialog from '@components/cigars/CigarDetailsDialog'
import RemoveCigarDialog from '@components/cigars/RemoveCigarDialog'
import MoveCigarDialog from '@components/cigars/MoveCigarDialog'
import BulkMoveCigarDialog from '@components/cigars/BulkMoveCigarDialog'
import { groupCigars } from '@components/humidors/HumidorContents'
import { useCollectionFilters } from '../features/collection/hooks/useCollectionFilters'
import { CollectionFiltersPanel } from '../features/collection/components/CollectionFilters'
import { CollectionList } from '../features/collection/components/CollectionList'
import { filtersToSearchParams } from '../features/collection/api/getCollection'
import { ExportModal } from '../shared/components/ExportModal'
import PageHeader from '@components/layout/PageHeader'
import { TOUCH_TARGET_MIN_PX } from '@components/layout/layoutConstants'
import { APP_SNACKBAR_CONTENT_SX, useAppSnackbarAnchorSx } from '@components/layout/appSnackbar'
import { trackFeature } from '@utils/trackFeature'
import { COLLECTION_SEARCH_DEBOUNCE_MS } from '../features/collection/constants/debounce'

const Collection: React.FC = () => {
  const snackbarAnchorSx = useAppSnackbarAnchorSx()

  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { isOnline } = useAppSelector((state) => state.offline)
  const { offlineCigars, isLoadingOffline, cacheCollection } = useOfflineCollection()
  const location = useLocation()
  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)
  const [editingCigar, setEditingCigar] = useState<Cigar | null>(null)
  const [removingCigar, setRemovingCigar] = useState<Cigar | null>(null)
  const [movingCigar, setMovingCigar] = useState<Cigar | null>(null)
  const [viewingCigar, setViewingCigar] = useState<Cigar | null>(null)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedCigarIds, setSelectedCigarIds] = useState<Set<string>>(new Set())
  const [bulkMovingCigars, setBulkMovingCigars] = useState<Cigar[]>([])
  const [showStats, setShowStats] = useState(false)
  const [smokingCigar, setSmokingCigar] = useState<Cigar | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const cigarFormOpenedRef = useRef(false)
  const skipInitialSearchTrack = useRef(true)
  const skipInitialFilterTrack = useRef(true)

  useEffect(() => {
    const state = location.state as { successMessage?: string; openAddCigar?: boolean } | null
    if (state?.successMessage) {
      setSuccessMessage(state.successMessage)
    }
    if (state?.openAddCigar) {
      cigarFormOpenedRef.current = true
      setEditingCigar(null)
      setShowForm(true)
      return
    }
    if (state?.successMessage) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  const {
    filters,
    setFilter,
    clearFilters,
    activeFilters,
    hasActiveFilters,
  } = useCollectionFilters()

  const searchParams = filtersToSearchParams(filters)

  const { data: fullCollection } = useGetCigarsQuery(
    { page: 1, limit: 5000, sortBy: 'brand', sortOrder: 'asc' },
    { skip: !isAuthenticated || !isOnline },
  )

  const { data: cigarsData, isLoading: cigarsLoading, error: cigarsError } = useSearchCigarsQuery(searchParams, {
    skip: !isAuthenticated || !isOnline,
  })

  useEffect(() => {
    if (isOnline && fullCollection?.cigars) {
      cacheCollection(fullCollection.cigars)
    }
  }, [fullCollection, isOnline, cacheCollection])

  const offlinePage = useMemo(() => {
    if (isOnline) return null
    const filtered = filterOfflineCigars(offlineCigars, filters)
    return paginateCigars(filtered, filters.page ?? 1, filters.limit ?? 20)
  }, [isOnline, offlineCigars, filters])

  const cigars = isOnline ? (cigarsData?.cigars ?? []) : (offlinePage?.cigars ?? [])
  const isLoading = isOnline ? cigarsLoading : isLoadingOffline
  const totalCount = isOnline ? (cigarsData?.total ?? 0) : (offlinePage?.total ?? 0)
  const totalPages = isOnline ? (cigarsData?.totalPages ?? 0) : (offlinePage?.totalPages ?? 0)

  const { data: brandsData } = useGetBrandsQuery({ search: '' }, {
    skip: !isAuthenticated || !isOnline,
  })

  const { data: collectionStats } = useGetCigarStatsQuery(undefined, {
    skip: !isAuthenticated || !isOnline,
  })

  const collectionTotal =
    collectionStats?.totalCount ??
    fullCollection?.total ??
    (isOnline ? 0 : offlineCigars.length)

  const filterTrackSignature = useMemo(() => {
    const clone = { ...filters }
    delete clone.search
    delete clone.page
    delete clone.limit
    delete clone.sortBy
    delete clone.sortOrder
    return JSON.stringify(clone)
  }, [filters])

  useEffect(() => {
    if (skipInitialSearchTrack.current) {
      skipInitialSearchTrack.current = false
      return
    }
    // Search input is already debounced 500ms before filters.search updates.
    const query = filters.search?.trim()
    if (query) {
      trackFeature('search_used', { query_length: query.length })
    }
  }, [filters.search])

  useEffect(() => {
    if (skipInitialFilterTrack.current) {
      skipInitialFilterTrack.current = false
      return
    }
    const timer = window.setTimeout(() => {
      const parsed = JSON.parse(filterTrackSignature) as Record<string, unknown>
      const keys = Object.keys(parsed).filter((key) => {
        const value = parsed[key]
        return value !== undefined && value !== null && value !== ''
      })
      if (keys.length === 0) {
        return
      }
      trackFeature('filter_used', {
        active_count: keys.length,
        keys: keys.join(','),
      })
    }, COLLECTION_SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [filterTrackSignature])

  const trackOverflow = useCallback((action: string) => {
    trackFeature('overflow_action', { action })
  }, [])

  const handleCreateCigar = () => {
    trackFeature('cta_cigar_add', { status: 'opened' })
    cigarFormOpenedRef.current = true
    setEditingCigar(null)
    setShowForm(true)
  }

  const handleEditCigar = (cigar: Cigar) => {
    setEditingCigar(cigar)
    setShowForm(true)
  }

  const handleDeleteCigar = (cigarId: string) => {
    const cigar = cigars.find(c => c.id === cigarId)
    if (cigar) {
      setRemovingCigar(cigar)
    }
  }

  const clearOpenAddCigarState = () => {
    const state = location.state as { openAddCigar?: boolean } | null
    if (!state?.openAddCigar) return
    navigate(location.pathname, { replace: true, state: {} })
  }

  const handleCloseForm = () => {
    if (cigarFormOpenedRef.current) {
      trackFeature('cta_cigar_add', { status: 'cancelled' })
      cigarFormOpenedRef.current = false
    }
    setShowForm(false)
    setEditingCigar(null)
    clearOpenAddCigarState()
  }

  const handleCigarSaved = () => {
    if (cigarFormOpenedRef.current) {
      trackFeature('cta_cigar_add', { status: 'saved' })
      cigarFormOpenedRef.current = false
    }
    clearOpenAddCigarState()
  }

  const handlePageChange = (value: number) => {
    setFilter('page', value)
  }

  const handleSortChange = (field: 'brand' | 'purchaseDate' | 'purchasePrice' | 'createdAt') => {
    if (filters.sortBy === field) {
      setFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setFilter('sortBy', field)
      setFilter('sortOrder', 'asc')
    }
  }

  const handleFilterChange = useCallback((key: keyof typeof filters, value: string | undefined) => {
    setFilter(key, value)
  }, [setFilter])

  const stats = collectionStats ? {
    totalCigars: collectionStats.totalCount,
    availableCigars: collectionStats.availableCount,
    totalValue: collectionStats.totalValue,
    averagePrice: collectionStats.averageValue
  } : null

  const handleToggleSelectionMode = () => {
    const next = !selectionMode
    trackOverflow(next ? 'select_start' : 'select_cancel')
    setSelectionMode(next)
    setSelectedCigarIds(new Set())
  }

  const handleToggleStats = () => {
    trackOverflow(showStats ? 'stats_hide' : 'stats_show')
    setShowStats(!showStats)
  }

  const handleOpenExport = () => {
    trackOverflow('export')
    setExportOpen(true)
  }

  const handleToggleCigarSelection = (cigarId: string) => {
    setSelectedCigarIds(prev => {
      const next = new Set(prev)
      if (next.has(cigarId)) {
        next.delete(cigarId)
      } else {
        next.add(cigarId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    const allIds = cigars.map(c => c.id)
    setSelectedCigarIds(new Set(allIds))
  }

  const handleClearSelection = () => {
    setSelectedCigarIds(new Set())
  }

  const handleBulkMove = () => {
    if (!cigars.length || selectedCigarIds.size === 0) return
    const selected = cigars.filter(c => selectedCigarIds.has(c.id))
    setBulkMovingCigars(selected)
  }

  const handleStartSessionFromCard = (cigar: Cigar) => {
    trackFeature('cta_smoke_start', { source: 'collection_card' })
    setSmokingCigar(cigar)
  }

  if (!isAuthenticated) {
    return (
      <>
        <Typography variant="h4" component="h1" gutterBottom>
          Collection
        </Typography>
        <Alert severity="info">
          Please log in to view your cigar collection.
        </Alert>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Collection"
        primaryAction={{
          label: 'Add cigar',
          icon: <AddIcon />,
          onClick: handleCreateCigar,
        }}
        overflowItems={[
          {
            id: 'stats',
            label: showStats ? 'Hide stats' : 'Show stats',
            icon: <InfoIcon fontSize="small" />,
            onClick: handleToggleStats,
          },
          {
            id: 'export',
            label: 'Export',
            icon: <ExportIcon fontSize="small" />,
            onClick: handleOpenExport,
          },
          {
            id: 'select',
            label: selectionMode ? 'Cancel select' : 'Select',
            icon: <SelectIcon fontSize="small" />,
            onClick: handleToggleSelectionMode,
          },
        ]}
      />

      {showStats && stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <StatusIcon color="primary" />
                  <Typography variant="h6">{stats.totalCigars}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Cigars
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <StatusIcon color="success" />
                  <Typography variant="h6">{stats.availableCigars}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <PriceIcon color="primary" />
                  <Typography variant="h6">${stats.totalValue.toFixed(2)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <PriceIcon color="primary" />
                  <Typography variant="h6">${stats.averagePrice.toFixed(2)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Average Price
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <CollectionFiltersPanel
        filters={filters}
        activeFilters={activeFilters}
        hasActiveFilters={hasActiveFilters}
        brands={brandsData?.map(b => ({ id: b.id, name: b.name }))}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
      />

      {collectionTotal > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="collection-insight">
          {totalCount} of {collectionTotal} cigars
        </Typography>
      )}

      <Box mb={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Brand ${filters.sortBy === 'brand' ? (filters.sortOrder === 'asc' ? '↑' : '↓') : ''}`}
            onClick={() => handleSortChange('brand')}
            color={filters.sortBy === 'brand' ? 'primary' : 'default'}
            variant={filters.sortBy === 'brand' ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Date ${filters.sortBy === 'purchaseDate' ? (filters.sortOrder === 'asc' ? '↑' : '↓') : ''}`}
            onClick={() => handleSortChange('purchaseDate')}
            color={filters.sortBy === 'purchaseDate' ? 'primary' : 'default'}
            variant={filters.sortBy === 'purchaseDate' ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Price ${filters.sortBy === 'purchasePrice' ? (filters.sortOrder === 'asc' ? '↑' : '↓') : ''}`}
            onClick={() => handleSortChange('purchasePrice')}
            color={filters.sortBy === 'purchasePrice' ? 'primary' : 'default'}
            variant={filters.sortBy === 'purchasePrice' ? 'filled' : 'outlined'}
          />
          <Chip
            label={`Added ${filters.sortBy === 'createdAt' ? (filters.sortOrder === 'asc' ? '↑' : '↓') : ''}`}
            onClick={() => handleSortChange('createdAt')}
            color={filters.sortBy === 'createdAt' ? 'primary' : 'default'}
            variant={filters.sortBy === 'createdAt' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      {selectionMode && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            maxWidth: '100%',
          }}
          data-testid="collection-selection-toolbar"
        >
          <Typography variant="body2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            {selectedCigarIds.size} of {cigars.length} selected
          </Typography>
          <Button size="small" onClick={handleSelectAll} sx={{ minHeight: TOUCH_TARGET_MIN_PX }}>
            Select All
          </Button>
          <Button size="small" onClick={handleClearSelection} sx={{ minHeight: TOUCH_TARGET_MIN_PX }}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<BulkMoveIcon />}
            disabled={selectedCigarIds.size === 0}
            onClick={handleBulkMove}
            sx={{ minHeight: TOUCH_TARGET_MIN_PX, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          >
            Move Selected ({selectedCigarIds.size})
          </Button>
        </Paper>
      )}

      <CollectionList
        cigars={cigars.length ? groupCigars(cigars) : undefined}
        isLoading={isLoading}
        error={isOnline ? cigarsError : undefined}
        page={filters.page || 1}
        totalPages={totalPages}
        totalCount={totalCount}
        hasActiveFilters={hasActiveFilters}
        onPageChange={handlePageChange}
        onEdit={selectionMode ? undefined : handleEditCigar}
        onDelete={selectionMode ? undefined : handleDeleteCigar}
        onMove={selectionMode ? undefined : (c) => setMovingCigar(c)}
        onView={selectionMode ? undefined : (c) => setViewingCigar(c)}
        onStartSession={selectionMode ? undefined : handleStartSessionFromCard}
        onAddCigar={handleCreateCigar}
        onClearFilters={clearFilters}
        onEmptyStateCta={() => trackFeature('empty_state_cta', { action: 'add_cigar' })}
        selectionMode={selectionMode}
        selectedCigarIds={selectedCigarIds}
        onToggleCigarSelection={handleToggleCigarSelection}
      />

      <CigarForm
        open={showForm}
        onClose={handleCloseForm}
        onSaved={handleCigarSaved}
        cigar={editingCigar}
      />

      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} dataType="collection" />

      <RemoveCigarDialog
        open={!!removingCigar}
        onClose={() => setRemovingCigar(null)}
        onSuccess={() => setSuccessMessage('Cigar removed from collection')}
        cigar={removingCigar}
      />

      <MoveCigarDialog
        open={!!movingCigar}
        onClose={() => setMovingCigar(null)}
        cigar={movingCigar}
      />

      <SessionForm
        open={!!smokingCigar}
        onClose={() => setSmokingCigar(null)}
        cigar={smokingCigar}
      />

      <CigarDetailsDialog
        open={!!viewingCigar}
        onClose={() => setViewingCigar(null)}
        cigar={viewingCigar}
        onEdit={(c) => { setViewingCigar(null); handleEditCigar(c) }}
        onStartSession={(c) => { setViewingCigar(null); setSmokingCigar(c) }}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={snackbarAnchorSx}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={APP_SNACKBAR_CONTENT_SX}>
          {successMessage}
        </Alert>
      </Snackbar>

      <BulkMoveCigarDialog
        open={bulkMovingCigars.length > 0}
        onClose={() => {
          setBulkMovingCigars([])
          setSelectedCigarIds(new Set())
          setSelectionMode(false)
        }}
        cigars={bulkMovingCigars}
      />
    </>
  )
}

export default Collection
